/**
 * AuthContext
 *
 * Global state management for user authentication.
 * Handles login, signup, logout, and session persistence via Firebase Auth.
 */

import { createContext, useContext, useReducer, useCallback, useEffect, ReactNode } from 'react';
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile as firebaseUpdateProfile,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../lib/firebase';

// State types
export interface AuthState {
  /** Current user (null if not authenticated) */
  user: User | null;

  /** Whether auth state is being checked on mount */
  isLoading: boolean;

  /** Whether authentication is in progress (login/signup) */
  isAuthenticating: boolean;

  /** Error message if any */
  error: string | null;

  /** Whether user's email is verified */
  emailVerified: boolean;
}

// Action types
export type AuthAction =
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'START_AUTH' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'LOGOUT' };

// Initial state
const initialState: AuthState = {
  user: null,
  isLoading: true, // Start as loading while checking session
  isAuthenticating: false,
  error: null,
  emailVerified: false,
};

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isLoading: false,
        emailVerified: action.payload?.emailVerified ?? false,
        error: null,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'START_AUTH':
      return {
        ...state,
        isAuthenticating: true,
        error: null,
      };

    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticating: false,
        emailVerified: action.payload.emailVerified,
        error: null,
      };

    case 'AUTH_ERROR':
      return {
        ...state,
        isAuthenticating: false,
        error: action.payload,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    case 'LOGOUT':
      return {
        ...initialState,
        isLoading: false,
      };

    default:
      return state;
  }
}

// Context type
interface AuthContextType {
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, displayName: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  updateUserProfile: (displayName: string, photoURL?: string) => Promise<void>;
  clearError: () => void;
}

// Create context
const AuthContext = createContext<AuthContextType | null>(null);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider
 *
 * Wraps the app and provides authentication state and methods.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Listen to auth state changes (Firebase handles session persistence)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      dispatch({ type: 'SET_USER', payload: user });
    });

    return unsubscribe;
  }, []);

  /**
   * Login with email and password
   */
  const login = useCallback(async (email: string, password: string) => {
    dispatch({ type: 'START_AUTH' });

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      dispatch({ type: 'AUTH_SUCCESS', payload: result.user });
    } catch (error) {
      const message = getAuthErrorMessage(error);
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  }, []);

  /**
   * Sign up with email and password
   */
  const signup = useCallback(async (email: string, password: string, displayName: string) => {
    dispatch({ type: 'START_AUTH' });

    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);

      // Update profile with display name
      await firebaseUpdateProfile(result.user, { displayName });

      // Send verification email
      await sendEmailVerification(result.user);

      dispatch({ type: 'AUTH_SUCCESS', payload: result.user });
    } catch (error) {
      const message = getAuthErrorMessage(error);
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  }, []);

  /**
   * Login with Google OAuth
   */
  const loginWithGoogle = useCallback(async () => {
    dispatch({ type: 'START_AUTH' });

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      dispatch({ type: 'AUTH_SUCCESS', payload: result.user });
    } catch (error) {
      const message = getAuthErrorMessage(error);
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  }, []);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      dispatch({ type: 'LOGOUT' });
    } catch (error) {
      const message = getAuthErrorMessage(error);
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  }, []);

  /**
   * Send password reset email
   */
  const sendPasswordReset = useCallback(async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      const message = getAuthErrorMessage(error);
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  }, []);

  /**
   * Send email verification
   */
  const sendVerificationEmail = useCallback(async () => {
    if (!state.user) {
      throw new Error('No user logged in');
    }

    try {
      await sendEmailVerification(state.user);
    } catch (error) {
      const message = getAuthErrorMessage(error);
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  }, [state.user]);

  /**
   * Update user profile
   */
  const updateUserProfile = useCallback(async (displayName: string, photoURL?: string) => {
    if (!state.user) {
      throw new Error('No user logged in');
    }

    try {
      await firebaseUpdateProfile(state.user, { displayName, photoURL });
      // Refresh user to get updated profile
      await state.user.reload();
      dispatch({ type: 'SET_USER', payload: auth.currentUser });
    } catch (error) {
      const message = getAuthErrorMessage(error);
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  }, [state.user]);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const value: AuthContextType = {
    state,
    login,
    signup,
    loginWithGoogle,
    logout,
    sendPasswordReset,
    sendVerificationEmail,
    updateUserProfile,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth hook
 *
 * Access authentication state and methods from any component.
 */
export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

/**
 * Convert Firebase Auth error codes to user-friendly messages
 */
function getAuthErrorMessage(error: unknown): string {
  if (!(error instanceof Error)) {
    return 'An unknown error occurred';
  }

  const code = (error as any).code;

  switch (code) {
    case 'auth/user-not-found':
      return 'No account found with this email';
    case 'auth/wrong-password':
      return 'Incorrect password';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters';
    case 'auth/invalid-email':
      return 'Invalid email address';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later';
    case 'auth/user-disabled':
      return 'This account has been disabled';
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled';
    case 'auth/popup-closed-by-user':
      return 'Sign-in popup was closed';
    default:
      return error.message || 'Authentication failed';
  }
}
